import { CAN_DOS, CEFR_ORDER } from '../data/candos.ts';
import {
  loadSettings, loadCanDoStates, allSessions,
  getAllItemStates, type SessionLogEntry,
} from '../data/stores.ts';
import type { ClozeState } from '../data/cloze-types.ts';
import type { HvptState } from '../data/hvpt-types.ts';

export function renderParcours(): HTMLElement {
  const root = document.createElement('section');
  root.className = 'flex flex-col gap-6 max-w-3xl mx-auto';
  root.innerHTML = '<p class="text-[color:var(--color-ink-muted)] mt-12 text-center">Chargement…</p>';
  init(root);
  return root;
}

async function init(root: HTMLElement): Promise<void> {
  const [settings, candoStates, sessions, clozeStates, hvptStates] = await Promise.all([
    loadSettings(),
    loadCanDoStates(),
    allSessions(),
    getAllItemStates<ClozeState>('cloze-state'),
    getAllItemStates<HvptState>('hvpt-state'),
  ]);

  root.innerHTML = '';
  root.append(renderHeader(settings.currentLevel));
  root.append(renderRadar(sessions, candoStates, clozeStates, hvptStates));
  root.append(renderCefrLadder(settings.currentLevel, candoStates));
  root.append(renderActivity(sessions));
}

function renderHeader(level: string): HTMLElement {
  const h = document.createElement('header');
  h.innerHTML = `
    <h1 class="text-3xl font-semibold tracking-tight">Mon parcours</h1>
    <p class="text-sm text-[color:var(--color-ink-muted)] mt-1">Niveau actuel&nbsp;: <span class="font-mono px-2 py-0.5 rounded bg-[color:var(--color-fjord-50)] text-[color:var(--color-fjord-700)]">${level}</span></p>
  `;
  return h;
}

interface TrackScore { label: string; tint: string; value: number; }

function renderRadar(
  sessions: SessionLogEntry[],
  candoStates: Map<string, { status: string }>,
  clozeStates: Map<string, ClozeState>,
  hvptStates: Map<string, HvptState>,
): HTMLElement {
  // Score 0..1 per track. Heuristics:
  //  vocab: sessions / 30 (clamped) — proxy until FSRS exposes coverage
  //  cloze: mean correct rate over attempted items
  //  hvpt:  mean correct rate over attempted pairs
  //  ecoute: sessions / 30
  //  tache: done-cando-count / total
  const vocabSessions = sessions.filter((s) => s.track === 'vocab').length;
  const clozeRate = meanRate([...clozeStates.values()]);
  const hvptRate = meanRate([...hvptStates.values()]);
  const ecouteSessions = sessions.filter((s) => s.track === 'ecoute').length;
  const taskDone = sessions.filter((s) => s.track === 'tache').length;

  const candoDoneInteraction = [...candoStates.values()].filter((s) => s.status === 'done').length;
  const taskScore = Math.min(1, (taskDone + candoDoneInteraction * 0.2) / 10);

  const scores: TrackScore[] = [
    { label: 'Vocabulaire',   tint: 'sage',   value: Math.min(1, vocabSessions / 14) },
    { label: 'Grammaire',     tint: 'peach',  value: clozeRate },
    { label: 'Prononciation', tint: 'mint',   value: hvptRate },
    { label: 'Écoute',        tint: 'butter', value: Math.min(1, ecouteSessions / 14) },
    { label: 'Tâche',         tint: 'rose',   value: taskScore },
  ];

  const card = document.createElement('div');
  card.className = 'rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] p-6';
  card.innerHTML = '<h2 class="text-lg font-semibold mb-1">Votre équilibre</h2><p class="text-xs text-[color:var(--color-ink-muted)] mb-4">Synthèse à partir de votre activité réelle (sessions, taux de réussite).</p>';

  const size = 300;
  const cx = size / 2;
  const cy = size / 2 + 12;
  const r = 110;
  const points = scores.map((s, i) => {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / scores.length;
    return [cx + Math.cos(angle) * r * s.value, cy + Math.sin(angle) * r * s.value] as const;
  });
  const grid = [0.25, 0.5, 0.75, 1].map((g) => {
    const poly = scores.map((_, i) => {
      const angle = -Math.PI / 2 + (i * 2 * Math.PI) / scores.length;
      return `${cx + Math.cos(angle) * r * g},${cy + Math.sin(angle) * r * g}`;
    }).join(' ');
    return `<polygon points="${poly}" fill="none" stroke="currentColor" stroke-opacity="0.15"/>`;
  }).join('');
  const labels = scores.map((s, i) => {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / scores.length;
    const lx = cx + Math.cos(angle) * (r + 24);
    const ly = cy + Math.sin(angle) * (r + 24);
    const anchor = Math.abs(Math.cos(angle)) < 0.1 ? 'middle' : Math.cos(angle) > 0 ? 'start' : 'end';
    return `<text x="${lx}" y="${ly}" text-anchor="${anchor}" font-size="11" fill="currentColor" opacity="0.75" dominant-baseline="middle">${s.label} ${Math.round(s.value * 100)}%</text>`;
  }).join('');
  const polyData = points.map(([x, y]) => `${x},${y}`).join(' ');
  card.innerHTML += `
    <svg viewBox="0 0 ${size} ${size + 24}" class="w-full text-[color:var(--color-ink)]">
      ${grid}
      <polygon points="${polyData}" fill="var(--color-fjord-500)" fill-opacity="0.3" stroke="var(--color-fjord-600)" stroke-width="1.5"/>
      ${points.map(([x, y]) => `<circle cx="${x}" cy="${y}" r="3" fill="var(--color-fjord-600)"/>`).join('')}
      ${labels}
    </svg>
  `;
  return card;
}

function meanRate(states: { attempts: number; correct: number }[]): number {
  if (states.length === 0) return 0;
  let attempts = 0;
  let correct = 0;
  for (const s of states) { attempts += s.attempts; correct += s.correct; }
  return attempts === 0 ? 0 : correct / attempts;
}

function renderCefrLadder(currentLevel: string, candoStates: Map<string, { status: string }>): HTMLElement {
  const sec = document.createElement('section');
  sec.className = 'rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] p-6';
  sec.innerHTML = '<h2 class="text-lg font-semibold mb-1">Échelle CECRL</h2><p class="text-xs text-[color:var(--color-ink-muted)] mb-4">Acquis validés par sous-niveau.</p>';

  const grouped = new Map<string, number>();
  const totals = new Map<string, number>();
  for (const c of CAN_DOS) {
    totals.set(c.cefr, (totals.get(c.cefr) ?? 0) + 1);
    if (candoStates.get(c.id)?.status === 'done') grouped.set(c.cefr, (grouped.get(c.cefr) ?? 0) + 1);
  }

  const list = document.createElement('div');
  list.className = 'flex flex-col gap-2';
  for (const level of CEFR_ORDER) {
    const total = totals.get(level) ?? 0;
    if (total === 0) continue;
    const done = grouped.get(level) ?? 0;
    const pct = total > 0 ? (done / total) * 100 : 0;
    const isCurrent = level === currentLevel;
    const row = document.createElement('div');
    row.className = 'flex items-center gap-3' + (isCurrent ? ' font-medium' : '');
    row.innerHTML = `
      <span class="w-12 font-mono text-xs px-2 py-0.5 rounded text-center ${isCurrent ? 'bg-[color:var(--color-fjord-600)] text-white' : 'bg-[color:var(--color-fjord-50)] text-[color:var(--color-fjord-700)]'}">${level}</span>
      <div class="flex-1 h-2 rounded-full bg-[color:var(--color-border)] overflow-hidden">
        <div class="h-full ${done > 0 ? 'bg-[color:var(--color-sage)]' : ''}" style="width:${pct}%"></div>
      </div>
      <span class="text-xs text-[color:var(--color-ink-muted)] w-12 text-right">${done}/${total}</span>
    `;
    list.append(row);
  }
  sec.append(list);
  return sec;
}

function renderActivity(sessions: SessionLogEntry[]): HTMLElement {
  const sec = document.createElement('section');
  sec.className = 'rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] p-6';
  sec.innerHTML = '<h2 class="text-lg font-semibold mb-1">Activité des 30 derniers jours</h2><p class="text-xs text-[color:var(--color-ink-muted)] mb-4">Une barre par jour, hauteur = sessions cumulées tous tracks confondus.</p>';

  const today = new Date();
  const buckets = new Array(30).fill(0).map((_, i) => {
    const d = new Date(today);
    d.setUTCDate(d.getUTCDate() - (29 - i));
    return { date: d.toISOString().slice(0, 10), count: 0 };
  });
  const bucketIdx = new Map(buckets.map((b, i) => [b.date, i]));
  for (const s of sessions) {
    const i = bucketIdx.get(s.date);
    if (i !== undefined) buckets[i]!.count++;
  }
  const max = Math.max(1, ...buckets.map((b) => b.count));
  const total = sessions.length;
  const last7 = buckets.slice(-7).reduce((a, b) => a + b.count, 0);

  const bars = document.createElement('div');
  bars.className = 'flex items-end gap-[2px] h-24';
  for (const b of buckets) {
    const bar = document.createElement('div');
    bar.className = 'flex-1 rounded-t';
    bar.style.background = b.count > 0 ? 'var(--color-fjord-500)' : 'var(--color-border)';
    bar.style.height = `${Math.max(4, (b.count / max) * 100)}%`;
    bar.title = `${b.date} — ${b.count} session${b.count > 1 ? 's' : ''}`;
    bars.append(bar);
  }
  sec.append(bars);

  const meta = document.createElement('p');
  meta.className = 'text-xs text-[color:var(--color-ink-muted)] mt-3 flex items-center justify-between';
  meta.innerHTML = `
    <span>${total} session${total > 1 ? 's' : ''} au total</span>
    <span>${last7} session${last7 > 1 ? 's' : ''} sur les 7 derniers jours</span>
  `;
  sec.append(meta);
  return sec;
}
